import { differenceInCalendarDays, format, formatDistanceToNowStrict, isThisYear, parseISO } from "date-fns";
import type { Health, Issue, Team } from "./types";

export const PRIORITIES = [
  { value: 0, label: "No priority" },
  { value: 1, label: "Urgent" },
  { value: 2, label: "High" },
  { value: 3, label: "Medium" },
  { value: 4, label: "Low" },
] as const;

export const priorityLabel = (p: number) => PRIORITIES.find((x) => x.value === p)?.label ?? "No priority";

/** Urgent first, then high … low, then no priority. */
export const priorityRank = (p: number) => (p === 0 ? 5 : p);

export function identifier(issue: Pick<Issue, "number" | "teamId">, teams: Record<string, Team>): string {
  const key = teams[issue.teamId]?.key ?? "?";
  return issue.number == null ? `${key}-…` : `${key}-${issue.number}`;
}

export function parseIdentifier(ident: string): { key: string; number: number } | null {
  const m = /^([A-Za-z][A-Za-z0-9]{0,6})-(\d+)$/.exec(ident.trim());
  return m ? { key: m[1].toUpperCase(), number: Number(m[2]) } : null;
}

export function ago(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = parseISO(ts);
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs >= 0 && secs < 45) return "just now";
  return formatDistanceToNowStrict(d, { addSuffix: true });
}

export function shortDate(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = parseISO(ts);
  return format(d, isThisYear(d) ? "MMM d" : "MMM d, yyyy");
}

export function longDate(ts: string | null | undefined): string {
  if (!ts) return "";
  return format(parseISO(ts), "EEE, MMM d, yyyy");
}

export function dateTime(ts: string | null | undefined): string {
  if (!ts) return "";
  return format(parseISO(ts), "MMM d, yyyy 'at' h:mm a");
}

/** "Due tomorrow", "3 days overdue". */
export function dueLabel(date: string | null): { text: string; tone: "overdue" | "soon" | "normal" } | null {
  if (!date) return null;
  const days = differenceInCalendarDays(parseISO(date), new Date());
  if (days < 0) return { text: days === -1 ? "Due yesterday" : `${-days} days overdue`, tone: "overdue" };
  if (days === 0) return { text: "Due today", tone: "soon" };
  if (days === 1) return { text: "Due tomorrow", tone: "soon" };
  if (days < 7) return { text: `Due in ${days} days`, tone: "soon" };
  return { text: `Due ${shortDate(date)}`, tone: "normal" };
}

export const HEALTH: Record<Health, { label: string; color: string }> = {
  on_track: { label: "On track", color: "#2F9E6E" },
  at_risk: { label: "At risk", color: "#E8A03A" },
  off_track: { label: "Off track", color: "#E5484D" },
};

export function money(cents: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);
}

/** "36m", "3h", "2d": how long since a timestamp, in the shortest unit that fits. */
export function sinceShort(ts: string | null | undefined): string {
  if (!ts) return "";
  const mins = Math.max(0, (Date.now() - Date.parse(ts)) / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = mins / 60;
  if (h < 24) return `${Math.round(h)}h`;
  const d = h / 24;
  if (d < 30) return `${Math.round(d)}d`;
  return `${Math.round(d / 30)}mo`;
}

/** "Good morning", "Good afternoon", "Good evening" by the local clock. */
export function greeting(date = new Date()): string {
  const h = date.getHours();
  if (h < 5) return "Still up";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function hours(h: number | null | undefined): string {
  if (h == null) return "–";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(h < 10 ? 1 : 0)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() || "?";
}

export function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

export const ESTIMATES: Record<Team["estimateScale"], { value: number; label: string }[]> = {
  none: [],
  linear: [1, 2, 3, 4, 5, 6, 7, 8].map((v) => ({ value: v, label: `${v} points` })),
  fibonacci: [1, 2, 3, 5, 8, 13, 21].map((v) => ({ value: v, label: `${v} points` })),
  tshirt: [
    { value: 1, label: "XS" },
    { value: 2, label: "S" },
    { value: 3, label: "M" },
    { value: 5, label: "L" },
    { value: 8, label: "XL" },
  ],
};

export function estimateLabel(value: number | null, scale: Team["estimateScale"] = "fibonacci"): string {
  if (value == null) return "";
  if (scale === "tshirt") return ESTIMATES.tshirt.find((e) => e.value === value)?.label ?? String(value);
  return String(value);
}
